import React, { useState } from 'react';
import { Card, Collapse, Space, Typography, Divider, Tag, Badge, Row, Col } from 'antd';
import {
  ArrowRightOutlined,
  LinkOutlined,
  WarningOutlined,
  CodeOutlined,
  BulbOutlined,
  QuestionCircleOutlined,
  ThunderboltOutlined,
  RollbackOutlined
} from '@ant-design/icons';
import { ConfigStep } from '../types';
import { MethodBadge, RiskLevelBadge } from '../Common';
import JsonViewer from '@src/components/Common/JsonViewer';

const { Text, Paragraph, Title } = Typography;
const { Panel } = Collapse;

interface ExecutionStepCardProps {
  step: ConfigStep;
  stepIndex: number;
  allSteps: ConfigStep[];
}

export const ExecutionStepCard: React.FC<ExecutionStepCardProps> = ({
  step,
  stepIndex,
  allSteps
}) => {
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [cardCollapsed, setCardCollapsed] = useState(true);

  // Get dependency step names
  const getDependencyNames = () => {
    if (!step.depends_on || step.depends_on.length === 0) return null;

    return step.depends_on.map((depIndex) => {
      const depStep = allSteps[depIndex];
      return depStep ? depStep.name : `Step ${depIndex}`;
    });
  };

  const dependencyNames = getDependencyNames();

  return (
    <div
      style={{
        marginBottom: '16px',
        borderRadius: '8px',
        border: '1px solid #e8e8e8',
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)',
        transition: 'transform var(--netorca-dur-mid) var(--netorca-ease-standard), box-shadow var(--netorca-dur-mid) var(--netorca-ease-standard)',
        background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
        overflow: 'hidden'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.05)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <Collapse
        ghost
        activeKey={cardCollapsed ? [] : ['main']}
        onChange={() => setCardCollapsed(!cardCollapsed)}
        style={{ background: 'transparent' }}
      >
        <Panel
          key="main"
          header={
            <div style={{ padding: '8px 0' }}>
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Space>
                  <Badge
                    count={stepIndex + 1}
                    style={{
                      backgroundColor: '#1890ff',
                      fontWeight: 600,
                      boxShadow: '0 2px 4px rgba(24, 144, 255, 0.3)'
                    }}
                  />
                  <Text strong style={{ fontSize: '14px' }}>{step.name}</Text>
                  <MethodBadge method={step.method} />
                  {step.explanation?.risk_level && (
                    <RiskLevelBadge level={step.explanation.risk_level} />
                  )}
                </Space>
                <Space size="small">
                  {step.skip_on_error && (
                    <Tag icon={<WarningOutlined />} color="orange">
                      Continue on error
                    </Tag>
                  )}
                  {step.store_response_as && (
                    <Tag icon={<CodeOutlined />} color="purple">
                      Store as: {step.store_response_as}
                    </Tag>
                  )}
                </Space>
              </Space>
            </div>
          }
          showArrow={true}
        >
      {/* Dependencies */}
      {dependencyNames && dependencyNames.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            <ArrowRightOutlined style={{ marginRight: '4px' }} />
            Depends on: {dependencyNames.join(', ')}
          </Text>
        </div>
      )}

      {/* Explanation Section - Horizontal Cards */}
      {step.explanation && (
        <div style={{ marginBottom: '16px' }}>
          <Row gutter={[12, 12]}>
            {step.explanation.what && (
              <Col span={step.explanation.rollback ? 12 : 8}>
                <Card
                  size="small"
                  style={{
                    background: 'linear-gradient(135deg, #e6f7ff 0%, #f0f5ff 100%)',
                    border: '1px solid #91d5ff',
                    borderRadius: '8px',
                    height: '100%',
                    transition: 'transform var(--netorca-dur-mid) var(--netorca-ease-standard), box-shadow var(--netorca-dur-mid) var(--netorca-ease-standard)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(24, 144, 255, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <BulbOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
                      <Text strong style={{ fontSize: '13px', color: '#1890ff' }}>What</Text>
                    </div>
                    <Text style={{ fontSize: '13px', lineHeight: '1.5' }}>{step.explanation.what}</Text>
                  </Space>
                </Card>
              </Col>
            )}
            {step.explanation.why && (
              <Col span={step.explanation.rollback ? 12 : 8}>
                <Card
                  size="small"
                  style={{
                    background: 'linear-gradient(135deg, #f9f0ff 0%, #f0f5ff 100%)',
                    border: '1px solid #d3adf7',
                    borderRadius: '8px',
                    height: '100%',
                    transition: 'transform var(--netorca-dur-mid) var(--netorca-ease-standard), box-shadow var(--netorca-dur-mid) var(--netorca-ease-standard)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(114, 46, 209, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <QuestionCircleOutlined style={{ color: '#722ed1', fontSize: '16px' }} />
                      <Text strong style={{ fontSize: '13px', color: '#722ed1' }}>Why</Text>
                    </div>
                    <Text style={{ fontSize: '13px', lineHeight: '1.5' }}>{step.explanation.why}</Text>
                  </Space>
                </Card>
              </Col>
            )}
            {step.explanation.impact && (
              <Col span={step.explanation.rollback ? 12 : 8}>
                <Card
                  size="small"
                  style={{
                    background: 'linear-gradient(135deg, #fff7e6 0%, #fffbe6 100%)',
                    border: '1px solid #ffd591',
                    borderRadius: '8px',
                    height: '100%',
                    transition: 'transform var(--netorca-dur-mid) var(--netorca-ease-standard), box-shadow var(--netorca-dur-mid) var(--netorca-ease-standard)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(250, 173, 20, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <ThunderboltOutlined style={{ color: '#faad14', fontSize: '16px' }} />
                      <Text strong style={{ fontSize: '13px', color: '#faad14' }}>Impact</Text>
                    </div>
                    <Text style={{ fontSize: '13px', lineHeight: '1.5' }}>{step.explanation.impact}</Text>
                  </Space>
                </Card>
              </Col>
            )}
            {step.explanation.rollback && (
              <Col span={12}>
                <Card
                  size="small"
                  style={{
                    background: 'linear-gradient(135deg, #fff2f0 0%, #fff1f0 100%)',
                    border: '1px solid #ffccc7',
                    borderRadius: '8px',
                    height: '100%',
                    transition: 'transform var(--netorca-dur-mid) var(--netorca-ease-standard), box-shadow var(--netorca-dur-mid) var(--netorca-ease-standard)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 77, 79, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <RollbackOutlined style={{ color: '#ff4d4f', fontSize: '16px' }} />
                      <Text strong style={{ fontSize: '13px', color: '#ff4d4f' }}>Rollback</Text>
                    </div>
                    <Text code style={{ fontSize: '12px', lineHeight: '1.5' }}>{step.explanation.rollback}</Text>
                  </Space>
                </Card>
              </Col>
            )}
          </Row>
        </div>
      )}

      <Divider style={{ margin: '8px 0' }} />

      {/* URL */}
      <div style={{ marginBottom: '8px' }}>
        <Text strong style={{ fontSize: '12px', marginRight: '8px' }}>
          <LinkOutlined /> Endpoint:
        </Text>
        <Text code style={{ fontSize: '11px', wordBreak: 'break-all' }}>
          {step.url}
        </Text>
      </div>

      {/* Collapsible sections */}
      <Collapse
        ghost
        activeKey={expandedKeys}
        onChange={(keys) => setExpandedKeys(keys as string[])}
      >
        {step.body && (
          <Panel header={<Text strong style={{ fontSize: '13px', color: '#1890ff' }}>📦 Request Body</Text>} key="body">
            <div style={{
              maxHeight: '200px',
              overflow: 'auto',
              padding: '8px',
              background: '#fafafa',
              borderRadius: '6px'
            }}>
              <JsonViewer data={step.body} fontSize="13px" lineHeight="1.6" />
            </div>
          </Panel>
        )}

        {step.headers && Object.keys(step.headers).length > 0 && (
          <Panel header={<Text strong style={{ fontSize: '13px', color: '#722ed1' }}>📋 Headers</Text>} key="headers">
            <div style={{
              maxHeight: '150px',
              overflow: 'auto',
              padding: '8px',
              background: '#fafafa',
              borderRadius: '6px'
            }}>
              <JsonViewer data={step.headers} fontSize="13px" lineHeight="1.6" />
            </div>
          </Panel>
        )}

        {step.use_response_from && Object.keys(step.use_response_from).length > 0 && (
          <Panel header={<Text strong style={{ fontSize: '13px', color: '#52c41a' }}>🔗 Response Mapping</Text>} key="response">
            <div style={{
              maxHeight: '150px',
              overflow: 'auto',
              padding: '8px',
              background: '#fafafa',
              borderRadius: '6px'
            }}>
              {Object.entries(step.use_response_from).map(([field, jsonPath]) => (
                <div key={field} style={{ marginBottom: '4px' }}>
                  <Text code style={{ fontSize: '11px' }}>{field}</Text>
                  <Text type="secondary" style={{ margin: '0 8px' }}>←</Text>
                  <Text code style={{ fontSize: '11px' }}>{jsonPath}</Text>
                </div>
              ))}
            </div>
          </Panel>
        )}
      </Collapse>
        </Panel>
      </Collapse>
    </div>
  );
};

export default ExecutionStepCard;
